import { useQuery } from '@zg-rentals/react-query5';
import {
  buildStaticMapProxyUrl,
  buildStaticMapUrl,
  buildStaticMapUrlForBounds,
  type StaticMapBounds,
} from '@/src/lib/utils/staticMap';

type UseStaticMapUrlOptions = {
  bounds: StaticMapBounds | undefined;
  width?: number;
  height?: number;
  scale?: number;
  zoom?: number;
};

const isProduction = process.env.NODE_ENV === 'production' && process.env.ZG_ENV !== 'development';

/**
 * Returns a Google Static Maps image URL for the given bounding box.
 *
 * Production / preview: returns a same-origin image proxy URL. The route signs
 * and fetches the Google Static Maps image server-side so preview/local referrer
 * restrictions do not break browser image requests.
 *
 * Dev: appends `NEXT_PUBLIC_GOOGLE_MAPS_KEY` directly to avoid routing every
 * thumbnail through Next while iterating locally.
 */
export const useStaticMapUrl = ({ bounds, width = 246, height = 182, scale = 2, zoom }: UseStaticMapUrlOptions) => {
  // Finite so a single legitimate 0 bound stays enabled; the all-zero box is
  // the missing-coordinates sentinel.
  const enabled = Boolean(
    bounds &&
    [bounds.minLat, bounds.maxLat, bounds.minLon, bounds.maxLon].every(Number.isFinite) &&
    !(bounds.minLat === 0 && bounds.maxLat === 0 && bounds.minLon === 0 && bounds.maxLon === 0),
  );

  const unsignedUrl =
    enabled && bounds
      ? zoom === undefined
        ? buildStaticMapUrlForBounds(bounds, { width, height, scale })
        : buildStaticMapUrl({
            lat: (bounds.minLat + bounds.maxLat) / 2,
            lon: (bounds.minLon + bounds.maxLon) / 2,
            width,
            height,
            scale,
            zoom,
          })
      : '';

  return useQuery({
    queryKey: ['staticMap', unsignedUrl],
    queryFn: () => {
      if (!isProduction && process.env.NEXT_PUBLIC_GOOGLE_MAPS_KEY) {
        return Promise.resolve(`${unsignedUrl}&key=${process.env.NEXT_PUBLIC_GOOGLE_MAPS_KEY}`);
      }
      return Promise.resolve(buildStaticMapProxyUrl(unsignedUrl));
    },
    enabled,
    staleTime: Infinity,
    gcTime: 10 * 60 * 1000,
  });
};
